/**
 * Pestaña "No hay en bodega" (faltantes) del tablero (réplica del `tab-faltante`
 * del SSR).
 *
 * Dos partes, igual que el SSR:
 *  1. **Lista de faltantes** (tabla): material, fecha, autor y, con permiso
 *     `retirar_faltante_lamina`, un botón "Retirar" (confirma + mutación).
 *  2. **Reporte rápido** (solo sede 41, con `add_pedidos_laminas`): 3 tarjetas
 *     (galvanizada/negra/HR) que crean un pedido único con `es_faltante` al elegir
 *     calibre + medida.
 *
 * La sede 41 se detecta por el nombre de la sede destino del board (como el SSR,
 * que usa `es_sede_41`). Presentacional + orquestación de hooks de datos; sin
 * `fetch`.
 */
import { useState } from "react";

import { Button } from "@/components/ui";
import { useAuth } from "@/lib/auth";

import {
  ADD_PERMISSION,
  FALTANTE_PRESETS,
  RETIRAR_FALTANTE_PERMISSION,
  type FaltantePreset,
} from "../constants";
import { pedidoErrorMessage } from "../format";
import {
  useCreatePedido,
  useRetirarFaltante,
} from "../hooks/usePedidoMutations";
import type { PedidoCreateBody, PedidoListItem } from "../types";
import { ConfirmActionDialog } from "./ConfirmActionDialog";

interface FaltantesTabProps {
  faltantes: PedidoListItem[];
  origenSedeId: number | null;
  destinoSedeId: number | null;
  origenNombre: string | null;
  destinoNombre: string | null;
}

export function FaltantesTab({
  faltantes,
  origenSedeId,
  destinoSedeId,
  origenNombre,
  destinoNombre,
}: FaltantesTabProps) {
  const { hasPermission } = useAuth();
  const canRetirar = hasPermission(RETIRAR_FALTANTE_PERMISSION);
  const canReportar = hasPermission(ADD_PERMISSION);

  // Sede 41 = la sede destino del board contiene "41" en su nombre (como el SSR).
  const esSede41 = Boolean(destinoNombre && destinoNombre.includes("41"));
  const puedeReportar =
    esSede41 && canReportar && origenSedeId !== null && destinoSedeId !== null;

  const retirar = useRetirarFaltante();
  const [retirarId, setRetirarId] = useState<number | null>(null);

  return (
    <div className="flex flex-col gap-4">
      {puedeReportar && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          Reporta rápidamente que un material <strong>no hay en bodega</strong>. El
          pedido llegará a <strong>{origenNombre}</strong> para que lo envíe.
        </div>
      )}

      <div>
        <h3 className="mb-2 text-sm font-semibold text-slate-700">
          Faltantes en {esSede41 ? destinoNombre : origenNombre}
        </h3>
        {faltantes.length > 0 ? (
          <div className="overflow-hidden rounded-md border border-slate-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-100 text-left text-slate-600">
                  <th className="px-3 py-2 font-medium">Material</th>
                  <th className="px-3 py-2 font-medium">Cantidad</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {faltantes.map((p) => (
                  <tr key={p.id} className="border-t border-slate-200">
                    <td className="px-3 py-2 font-semibold text-slate-800">
                      {p.categoria}
                      {p.calibre ? ` · Cal ${p.calibre}` : ""}
                      {p.medida ? ` · ${p.medida}` : ""}
                    </td>
                    <td className="px-3 py-2 text-slate-500">{p.cantidad}</td>
                    <td className="px-3 py-2 text-right">
                      {canRetirar && (
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            retirar.reset();
                            setRetirarId(p.id);
                          }}
                        >
                          Retirar
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="py-4 text-center text-sm text-slate-400">
            No hay faltantes pendientes.
          </p>
        )}
      </div>

      {puedeReportar && origenSedeId !== null && destinoSedeId !== null && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FALTANTE_PRESETS.map((preset) => (
            <FaltantePresetCard
              key={preset.id}
              preset={preset}
              origenSedeId={origenSedeId}
              destinoSedeId={destinoSedeId}
            />
          ))}
        </div>
      )}

      <ConfirmActionDialog
        open={retirarId !== null}
        onOpenChange={(o) => {
          if (!o) {
            setRetirarId(null);
            retirar.reset();
          }
        }}
        title="Retirar aviso de faltante"
        description="¿Retirar este aviso de faltante? Dejará de aparecer en la lista."
        confirmLabel="Retirar"
        confirmVariant="danger"
        isPending={retirar.isPending}
        error={retirar.isError ? retirar.error : null}
        onConfirm={() => {
          if (retirarId !== null) {
            retirar.mutate(retirarId, { onSuccess: () => setRetirarId(null) });
          }
        }}
      />
    </div>
  );
}

interface FaltantePresetCardProps {
  preset: FaltantePreset;
  origenSedeId: number;
  destinoSedeId: number;
}

/** Tarjeta de reporte rápido de un material (galvanizada/negra/HR). */
function FaltantePresetCard({
  preset,
  origenSedeId,
  destinoSedeId,
}: FaltantePresetCardProps) {
  const create = useCreatePedido();
  const [calibre, setCalibre] = useState("");
  const [medida, setMedida] = useState("");

  const errorText = create.isError ? pedidoErrorMessage(create.error) : null;

  function reportar(event: React.FormEvent) {
    event.preventDefault();
    if (!calibre || !medida) {
      return;
    }
    const body: PedidoCreateBody = {
      tipo_pedido: "unico",
      origen_sede_id: origenSedeId,
      destino_sede_id: destinoSedeId,
      categoria: preset.categoria,
      calibre,
      medida,
      tubo: "",
      descripcion: "Faltante en bodega",
      cantidad: "1",
      es_faltante: true,
    };
    create.mutate(body, {
      onSuccess: () => {
        setCalibre("");
        setMedida("");
      },
    });
  }

  const selectClass =
    "block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500";

  return (
    <form
      onSubmit={reportar}
      className="flex flex-col gap-3 rounded-lg border-2 border-slate-200 bg-white p-4"
    >
      <div>
        <div className="font-bold text-slate-800">{preset.titulo}</div>
        <div className="text-xs text-slate-500">{preset.subtitulo}</div>
      </div>
      <div className="flex flex-col gap-1">
        <label
          htmlFor={`fl-${preset.id}-calibre`}
          className="text-xs font-bold uppercase tracking-wide text-slate-600"
        >
          Calibre *
        </label>
        <select
          id={`fl-${preset.id}-calibre`}
          className={selectClass}
          value={calibre}
          onChange={(e) => setCalibre(e.target.value)}
        >
          <option value="">Seleccione</option>
          {preset.calibres.map((c) => (
            <option key={c} value={c}>
              CAL {c}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-col gap-1">
        <label
          htmlFor={`fl-${preset.id}-medida`}
          className="text-xs font-bold uppercase tracking-wide text-slate-600"
        >
          Medida *
        </label>
        <select
          id={`fl-${preset.id}-medida`}
          className={selectClass}
          value={medida}
          onChange={(e) => setMedida(e.target.value)}
        >
          <option value="">Seleccione</option>
          {preset.medidas.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </div>
      {errorText && (
        <p role="alert" className="text-sm text-red-600">
          {errorText}
        </p>
      )}
      <Button
        type="submit"
        variant="danger"
        size="sm"
        disabled={create.isPending || !calibre || !medida}
      >
        {create.isPending ? "Reportando…" : "Reportar faltante"}
      </Button>
    </form>
  );
}
